import React, { useEffect, useState } from 'react';
import { X, CheckCircle2, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { clsx } from 'clsx';
import { notifications, NotificationType } from '@/utils/notifications';

interface Toast {
  id: number;
  message: string;
  type: NotificationType;
}

export const Toaster: React.FC = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const unsubscribe = notifications.subscribe((message, type) => {
      const id = Date.now();
      setToasts((prev) => [...prev, { id, message, type }]);

      // Auto-dismiss after 5 seconds
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 5000);
    });

    return unsubscribe;
  }, []);

  const removeToast = (id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <div className="fixed bottom-6 right-6 z-[200] flex flex-col gap-3 max-w-sm w-full pointer-events-none">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={clsx(
            "pointer-events-auto flex items-start gap-4 p-4 modal-surface border shadow-modal animate-in slide-in-from-right-8 fade-in duration-300",
            toast.type === 'success' && "bg-[#0d121b]/95 border-emerald-500/20 text-emerald-400",
            toast.type === 'error' && "bg-[#0d121b]/95 border-rose-500/20 text-rose-400",
            toast.type === 'warning' && "bg-[#0d121b]/95 border-amber-500/20 text-amber-400",
            toast.type === 'info' && "bg-[#0d121b]/95 border-blue-500/20 text-blue-400"
          )}
        >
          <div className="shrink-0 mt-0.5">
            {toast.type === 'success' && <CheckCircle2 size={18} />}
            {toast.type === 'error' && <AlertCircle size={18} />}
            {toast.type === 'warning' && <AlertTriangle size={18} />}
            {toast.type === 'info' && <Info size={18} />}
          </div>
          
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold leading-relaxed">{toast.message}</p>
          </div>

          <button
            onClick={() => removeToast(toast.id)}
            className="shrink-0 p-1 hover:bg-white/5 rounded-lg transition-colors text-slate-500 hover:text-white"
          >
            <X size={16} />
          </button>
        </div>
      ))}
    </div>
  );
};
